/**
 * @file The one line that says where a Signify fetch has got to, plus any job-level error.
 * Consumer: `SignifyFetchJobProgress`.
 * This is the one component whose output depends on every part, so it subscribes to the whole parts
 * collection on purpose: its render is a walk over frozen snapshot leaves producing one line of
 * text, which is nothing next to the virtual table's layout pass. Every number comes from
 * `deriveSignifyFetchJobProgress`, so the header, the footer and the menu label can never disagree.
 * The five-second clock is the only effect here, and it subscribes to the clock and nothing else —
 * the derived line is computed inline at render from whatever `now` holds.
 */

import { FC, useEffect, useState } from 'react';
import { Typography } from 'antd';
import { useSnapshot } from 'valtio';
import FlexBox from '@/components/atoms/FlexBox';
import FlexCol from '@/components/atoms/FlexCol';
import type { SignifyFetchJob, SignifyFetchPart } from '#types';
import type { CollectionStore, DocumentStore } from '@/store';
import { deriveSignifyFetchJobProgress } from '../use/services';

const { Text } = Typography;

const CLOCK_INTERVAL_MS = 5_000;
const MINUTE_MS = 60_000;

type ProgressRunTone = 'secondary' | 'warning' | 'danger' | undefined;

type SignifyFetchJobProgressHeaderProps = {
  jobStore: DocumentStore<SignifyFetchJob>;
  partsStore: CollectionStore<SignifyFetchPart>;
};

const SignifyFetchJobProgressHeader: FC<SignifyFetchJobProgressHeaderProps> = ({
  jobStore,
  partsStore,
}) => {
  const [now, setNow] = useState(Date.now);
  const jobSnap = useSnapshot(jobStore.state);
  const partsSnap = useSnapshot(partsStore.state);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), CLOCK_INTERVAL_MS);
    return () => clearInterval(timer);
  }, []);

  const job = jobSnap.data;
  const parts = partsSnap.docIds.flatMap((partId) => {
    const entry = partsSnap.docs.get(partId);
    return entry ? [entry.data] : [];
  });
  const progress = deriveSignifyFetchJobProgress(
    job === null
      ? null
      : { phase: job.phase, heartbeatAt: job.heartbeatAt, concurrency: job.concurrency },
    parts,
    now,
  );

  const runs: Array<{ key: string; text: string; tone: ProgressRunTone }> = [
    { key: 'fetched', text: `${progress.done} of ${progress.total} fetched`, tone: undefined },
    ...(progress.failed > 0
      ? [{ key: 'failed', text: `${progress.failed} failed`, tone: 'danger' as const }]
      : []),
    ...(progress.ambiguous > 0
      ? [{
          key: 'ambiguous',
          text: `${progress.ambiguous} need${progress.ambiguous === 1 ? 's' : ''} a choice`,
          tone: 'warning' as const,
        }]
      : []),
    ...(progress.etaMs !== null
      ? [{
          key: 'eta',
          text: progress.etaMs < MINUTE_MS
            ? 'less than a minute left'
            : `about ${Math.round(progress.etaMs / MINUTE_MS)} min left`,
          tone: 'secondary' as const,
        }]
      : []),
    ...(progress.status === 'running' && progress.unfetched > 0 && progress.etaMs === null
      ? [{ key: 'estimating', text: 'estimating time left', tone: 'secondary' as const }]
      : []),
    ...(progress.status === 'queued'
      ? [{ key: 'queued', text: 'queued behind another batch', tone: 'secondary' as const }]
      : []),
    ...(progress.status === 'stalled' && job !== null
      ? [{
          key: 'stalled',
          text: `stalled — no progress for ${Math.max(1, Math.round((now - job.heartbeatAt) / MINUTE_MS))} min`,
          tone: 'warning' as const,
        }]
      : []),
    ...(progress.slowedCap !== null
      ? [{
          key: 'slowed',
          text: `slowed by Signify, ${progress.slowedCap} requests at a time`,
          tone: 'warning' as const,
        }]
      : []),
    ...(progress.status === 'completed'
      ? [{ key: 'finished', text: 'finished', tone: 'secondary' as const }]
      : []),
    ...(progress.status === 'cancelled'
      ? [{ key: 'cancelled', text: 'cancelled', tone: 'secondary' as const }]
      : []),
  ];

  return (
    <FlexCol gap={5}>
      <FlexBox justifyStart gap={5} wrap>
        {runs.map((run, index) => (
          <FlexBox key={run.key} justifyStart gap={5} noGrow>
            {index > 0 && <Text type="secondary">·</Text>}
            <Text type={run.tone}>{run.text}</Text>
          </FlexBox>
        ))}
      </FlexBox>
      {job?.lastError ? <Text type="danger">{job.lastError}</Text> : null}
    </FlexCol>
  );
};

export default SignifyFetchJobProgressHeader;
